import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, CreditCard, AlertTriangle, Printer } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const HostelDamage = () => {
  const [damages, setDamages] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [banks, setBanks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [selectedDamage, setSelectedDamage] = useState(null);
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [selectedBankId, setSelectedBankId] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [paidDamageData, setPaidDamageData] = useState(null);
  const receiptRef = useRef();
  
  const [formData, setFormData] = useState({
    damage_number: `DMG-${Date.now()}`,
    hostel_id: '',
    hostel_name: '',
    room_id: '',
    room_number: '',
    student_id: '',
    student_name: '',
    damage_date: new Date().toISOString().split('T')[0],
    damage_description: '',
    damage_cost: 0,
    responsible_person: '',
    reported_by: 'Admin'
  });

  useEffect(() => { fetchDamages(); fetchHostels(); fetchBanks(); }, []);

  const fetchDamages = async () => {
    try {
      const response = await apiClient.get('/api/hostel/damages');
      setDamages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchHostels = async () => {
    try {
      const response = await apiClient.get('/api/hostel/hostels');
      setHostels(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await apiClient.get('/api/accounts/banks');
      setBanks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchRooms = async (hostelId) => {
    try {
      const response = await apiClient.get(`/api/hostel/rooms?hostel_id=${hostelId}`);
      setRooms(Array.isArray(response.data) ? response.data : []);
      setStudents([]);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchStudentsByRoom = async (roomId) => {
    try {
      const response = await apiClient.get(`/api/hostel/students?room_id=${roomId}&status=Active`);
      setStudents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
      setStudents([]);
    }
  };

  const handleHostelChange = (hostelId) => {
    const hostel = hostels.find(h => h.id === hostelId);
    if (hostel) {
      setFormData({
        ...formData,
        hostel_id: hostelId,
        hostel_name: hostel.hostel_name,
        room_id: '',
        room_number: '',
        student_id: '',
        student_name: ''
      });
      fetchRooms(hostelId);
    }
  };

  const handleRoomChange = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    if (room) {
      setFormData({
        ...formData,
        room_id: roomId,
        room_number: room.room_number,
        student_id: '',
        student_name: ''
      });
      fetchStudentsByRoom(roomId);
    }
  };

  const handleStudentChange = (studentId) => {
    const student = students.find(s => s.id === studentId);
    if (student) {
      setFormData({
        ...formData,
        student_id: studentId,
        student_name: student.student_name,
        responsible_person: student.student_name
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiClient.post('/api/hostel/damages', formData);
      if (response.status === 200 || response.data) {
        alert('Damage reported successfully!' + (formData.student_id ? ' Fine has been issued to the student.' : ''));
        fetchDamages();
        handleCloseModal();
      }
    } catch (error) {
      alert('Failed to report damage');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedDamage) return;
    
    const bankModes = ['UPI', 'Bank Transfer', 'NEFT', 'RTGS', 'Cheque', 'Card'];
    if (bankModes.includes(paymentMode) && !selectedBankId) {
      alert('Please select a bank account for this payment mode');
      return;
    }
    
    setLoading(true);
    try {
      let url = `/api/hostel/damages/${selectedDamage.id}/pay?payment_mode=${paymentMode}&payment_date=${paymentDate}`;
      if (selectedBankId && bankModes.includes(paymentMode)) {
        url += `&bank_id=${selectedBankId}`;
      }
      if (transactionId) {
        url += `&transaction_id=${encodeURIComponent(transactionId)}`;
      }
      const response = await apiClient.post(url);
      if (response.data) {
        const selectedBank = banks.find(b => b.id === selectedBankId);
        setPaidDamageData({
          ...selectedDamage,
          payment_mode: paymentMode,
          transaction_id: transactionId,
          bank_name: selectedBank?.bank_name || '',
          payment_date: new Date().toISOString()
        });
        fetchDamages();
        setIsPayModalOpen(false);
        setIsPrintModalOpen(true);
      }
    } catch (error) {
      alert('Payment failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = receiptRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Damage Receipt - ${paidDamageData?.damage_number || ''}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .text-center { text-align: center; }
            .border-b { border-bottom: 1px solid #ddd; padding-bottom: 15px; margin-bottom: 15px; }
            .font-bold { font-weight: bold; }
            .text-xl { font-size: 1.25rem; }
            .text-sm { font-size: 0.875rem; }
            .text-xs { font-size: 0.75rem; }
            .text-gray-500 { color: #6b7280; }
            .text-gray-600 { color: #4b5563; }
            .text-gray-400 { color: #9ca3af; }
            .text-green-600 { color: #16a34a; }
            .text-red-600 { color: #dc2626; }
            .space-y-3 > * + * { margin-top: 0.75rem; }
            .flex { display: flex; }
            .justify-between { justify-content: space-between; }
            .border-t { border-top: 1px solid #ddd; padding-top: 15px; margin-top: 15px; }
            .text-right { text-align: right; }
            .max-w-200 { max-width: 200px; }
            .text-lg { font-size: 1.125rem; }
            .mt-6 { margin-top: 1.5rem; }
            .pt-4 { padding-top: 1rem; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const openPayModal = (damage) => {
    setSelectedDamage(damage);
    setPaymentMode('Cash');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setSelectedBankId('');
    setTransactionId('');
    setIsPayModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setStudents([]);
    setRooms([]);
    setFormData({
      damage_number: `DMG-${Date.now()}`,
      hostel_id: '',
      hostel_name: '',
      room_id: '',
      room_number: '',
      student_id: '',
      student_name: '',
      damage_date: new Date().toISOString().split('T')[0],
      damage_description: '',
      damage_cost: 0,
      responsible_person: '',
      reported_by: 'Admin'
    });
  };

  const printDamageReceipt = (damage) => {
    setPaidDamageData(damage);
    setIsPrintModalOpen(true);
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      'Reported': 'bg-red-100 text-red-800',
      'Under Repair': 'bg-yellow-100 text-yellow-800',
      'Repaired': 'bg-blue-100 text-blue-800',
      'Paid': 'bg-green-100 text-green-800'
    };
    return statusStyles[status] || 'bg-gray-100 text-gray-800';
  };

  const isBankPayment = ['UPI', 'Bank Transfer', 'NEFT', 'RTGS', 'Cheque', 'Card'].includes(paymentMode);

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            Hostel Damages
          </CardTitle>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />Report Damage
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3">Damage #</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Hostel</th>
                  <th className="text-left p-3">Room</th>
                  <th className="text-left p-3">Student</th>
                  <th className="text-left p-3">Description</th>
                  <th className="text-right p-3">Cost</th>
                  <th className="text-center p-3">Payment</th>
                  <th className="text-center p-3">Status</th>
                  <th className="text-center p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {damages.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center p-8 text-gray-500">
                      No damages reported.
                    </td>
                  </tr>
                ) : (
                  damages.map((damage) => (
                    <tr key={damage.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{damage.damage_number}</td>
                      <td className="p-3">{formatDate(damage.damage_date)}</td>
                      <td className="p-3">{damage.hostel_name}</td>
                      <td className="p-3">{damage.room_number}</td>
                      <td className="p-3">{damage.student_name || '-'}</td>
                      <td className="p-3 max-w-xs truncate">{damage.damage_description}</td>
                      <td className="p-3 text-right text-red-600 font-semibold">₹{damage.damage_cost?.toLocaleString('en-IN')}</td>
                      <td className="p-3 text-center text-sm">
                        {damage.payment_mode ? (
                          <span className="text-gray-600">{damage.payment_mode}</span>
                        ) : '-'}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(damage.status)}`}>
                          {damage.status}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex gap-1 justify-center">
                          {damage.status !== 'Paid' && damage.student_id && (
                            <Button size="sm" variant="outline" onClick={() => openPayModal(damage)}>
                              <CreditCard className="w-4 h-4 mr-1" />Pay
                            </Button>
                          )}
                          {damage.status === 'Paid' && (
                            <Button size="sm" variant="ghost" onClick={() => printDamageReceipt(damage)}>
                              <Printer className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Report Damage Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Report Damage</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Damage Number</Label>
                <Input value={formData.damage_number} disabled />
              </div>
              <div>
                <Label>Damage Date *</Label>
                <Input
                  type="date"
                  required
                  value={formData.damage_date}
                  onChange={(e) => setFormData({ ...formData, damage_date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hostel *</Label>
                <Select value={formData.hostel_id} onValueChange={handleHostelChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select hostel" />
                  </SelectTrigger>
                  <SelectContent>
                    {hostels.map((h) => (
                      <SelectItem key={h.id} value={h.id}>{h.hostel_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Room *</Label>
                <Select value={formData.room_id} onValueChange={handleRoomChange} disabled={!formData.hostel_id}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select room" />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.room_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Responsible Student (Optional)</Label>
              <Select value={formData.student_id} onValueChange={handleStudentChange} disabled={!formData.room_id}>
                <SelectTrigger>
                  <SelectValue placeholder={students.length > 0 ? "Select student" : "No students in this room"} />
                </SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.student_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                If student is selected, a fine will be automatically issued.
              </p>
            </div>

            <div>
              <Label>Damage Description *</Label>
              <Input
                required
                value={formData.damage_description}
                onChange={(e) => setFormData({ ...formData, damage_description: e.target.value })}
                placeholder="Describe the damage"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Damage Cost (₹) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  required
                  value={formData.damage_cost}
                  onChange={(e) => setFormData({ ...formData, damage_cost: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Reported By</Label>
                <Input
                  value={formData.reported_by}
                  onChange={(e) => setFormData({ ...formData, reported_by: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Report Damage'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={isPayModalOpen} onOpenChange={setIsPayModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pay Damage Fine</DialogTitle>
          </DialogHeader>
          {selectedDamage && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p><strong>Damage:</strong> {selectedDamage.damage_description}</p>
                <p><strong>Student:</strong> {selectedDamage.student_name || 'N/A'}</p>
                <p><strong>Room:</strong> {selectedDamage.room_number}</p>
                <p className="text-xl font-bold text-red-600">
                  Amount: ₹{selectedDamage.damage_cost?.toLocaleString('en-IN')}
                </p>
              </div>

              <div>
                <Label>Payment Date *</Label>
                <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} max={new Date().toISOString().split('T')[0]} data-testid="damage-payment-date" />
              </div>

              <div>
                <Label>Payment Mode *</Label>
                <Select value={paymentMode} onValueChange={(v) => { setPaymentMode(v); setSelectedBankId(''); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="NEFT">NEFT</SelectItem>
                    <SelectItem value="RTGS">RTGS</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {isBankPayment && (
                <>
                  <div>
                    <Label>Select Bank Account *</Label>
                    <Select value={selectedBankId} onValueChange={setSelectedBankId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select bank account" />
                      </SelectTrigger>
                      <SelectContent>
                        {banks.map((bank) => (
                          <SelectItem key={bank.id} value={bank.id}>
                            {bank.bank_name} - {bank.account_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Transaction ID / Reference Number</Label>
                    <Input
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      placeholder="Enter transaction ID or reference number"
                    />
                  </div>
                </>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsPayModalOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handlePayment} 
                  disabled={loading || (isBankPayment && !selectedBankId)} 
                  className="bg-green-600 hover:bg-green-700"
                >
                  {loading ? 'Processing...' : `Pay ₹${selectedDamage.damage_cost?.toLocaleString('en-IN')}`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Receipt Modal */}
      <Dialog open={isPrintModalOpen} onOpenChange={setIsPrintModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Payment Receipt</DialogTitle>
          </DialogHeader>
          
          {/* Printable Receipt */}
          <div ref={receiptRef} className="p-6 bg-white">
            <div className="text-center border-b pb-4 mb-4">
              <h2 className="text-xl font-bold">HOSTEL DAMAGE FINE RECEIPT</h2>
              <p className="text-sm text-gray-500">Payment Confirmation</p>
            </div>
            
            {paidDamageData && (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Receipt No:</span>
                  <span className="font-medium">{paidDamageData.damage_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span>{formatDate(paidDamageData.payment_date || paidDamageData.damage_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Student:</span>
                  <span>{paidDamageData.student_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Hostel:</span>
                  <span>{paidDamageData.hostel_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Room:</span>
                  <span>{paidDamageData.room_number}</span>
                </div>
                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Damage:</span>
                    <span className="text-right max-w-[200px]">{paidDamageData.damage_description}</span>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Mode:</span>
                  <span>{paidDamageData.payment_mode || 'Cash'}</span>
                </div>
                {paidDamageData.bank_name && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bank:</span>
                    <span>{paidDamageData.bank_name}</span>
                  </div>
                )}
                {paidDamageData.transaction_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction ID:</span>
                    <span>{paidDamageData.transaction_id}</span>
                  </div>
                )}
                <div className="border-t pt-3 mt-3 flex justify-between text-lg font-bold">
                  <span>Amount Paid:</span>
                  <span className="text-green-600">₹{paidDamageData.damage_cost?.toLocaleString('en-IN')}</span>
                </div>
                <div className="text-center text-xs text-gray-400 mt-6 pt-4 border-t">
                  <p>Thank you for your payment</p>
                  <p>This is a computer generated receipt</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsPrintModalOpen(false)}>
              Close
            </Button>
            <Button onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />Print Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HostelDamage;
